/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { bindQuerySelector, el, hide, jsx, show } from '@amazon/vinyl-tsx'
import { getAuth, notSignedInOrPending } from '@/auth/authRef'
import { createDisposer, logDebug, noop } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { AuthStatus } from '@/auth/auth'
import { getString, getStringAsync } from '@/auth/i18n/bundle.generated'
import { qrCodeSvg } from '@/components/qrCode'
import type { Territory } from '@amzn/vinyl-amzn'
import { currentTerritory, retailDomain } from '@/marketplace'

const { target, errorHandler } = ctx('CodeLinkCallout')

const CALLOUT_FONTS = [
    "400 1em 'Amazon Ember'",
    '400 1em Ember',
    "700 1em 'Ember Modern Display'",
    "400 1em 'Ember Modern Display'",
]

const CONTENT_HOLD_CAP_SECONDS = 1

const CODE_ENTRY_PATH = '/code'

export function codeEntryLabel(territory: Territory): string {
    return `${retailDomain(territory).replace(/^www\./, '')}${CODE_ENTRY_PATH}`
}

export function codeEntryUrl(territory: Territory, publicCode: string): string {
    return `https://${retailDomain(territory)}${CODE_ENTRY_PATH}?cbl-code=${encodeURIComponent(publicCode)}&cbl-status=redirect_get_request`
}

/**
 * Keeps the callout hidden (hidden nodes don't count as layout shift) until its strings and fonts
 * have landed, or the cap passes, so a slow or failed load can never leave the screen blank.
 */
function holdUntilContentLoaded(callout: HTMLElement): () => void {
    const reveal = () => callout.classList.remove('awaitingContent')
    callout.classList.add('awaitingContent')
    const timer = setTimeout(reveal, CONTENT_HOLD_CAP_SECONDS * 1000)
    const loads: Promise<unknown>[] = [getStringAsync('dmusic_public_code_prompt_title')]
    if ('fonts' in document) loads.push(...CALLOUT_FONTS.map((font) => document.fonts.load(font)))
    Promise.all(loads.map((load) => load.then(noop, noop)))
        .then(reveal)
        .catch(errorHandler)
    return () => clearTimeout(timer)
}

export function CodeLinkCallout() {
    const entryLabel = codeEntryLabel(currentTerritory())
    return (
        <div
            id="codeLinkCallout"
            className="codeLinkCallout"
            onConnect={(callout) => {
                const { add, dispose } = createDisposer()
                add(holdUntilContentLoaded(callout))

                const auth = getAuth()
                const signIn = () => {
                    // CBL Auth needs to call signIn to initialize the code.
                    displayPublicCodePrompt(null)
                    auth.signIn()
                }

                add(
                    auth.status.onData((value) => {
                        if (value === AuthStatus.SIGNED_OUT) {
                            setTimeout(signIn)
                        }
                    })
                )
                return dispose
            }}
            visible={notSignedInOrPending()}
        >
            <div
                className="logo"
                role="img"
                aria-label={getString('dmusic_public_code_prompt_logo_accessibility_label')}
            />

            <h1>{getString('dmusic_public_code_prompt_title')}</h1>
            <div className="columns">
                <section className="scanColumn">
                    <h2>{getString('dmusic_public_code_prompt_scan_title')}</h2>
                    <p>{getString('dmusic_public_code_prompt_scan_instructions')}</p>
                    <a
                        className="qrCode"
                        target="_blank"
                        data-external-direct
                        aria-label={entryLabel}
                    ></a>
                </section>
                <section className="codeColumn">
                    <h2>{getString('dmusic_public_code_prompt_other_title')}</h2>
                    <p className="instructions">
                        {getString('dmusic_public_code_prompt_code_instructions_prefix')}{' '}
                        <a className="signInLink" target="_blank" data-external-direct>
                            {entryLabel}
                        </a>
                    </p>
                    <span className="codeLink" />
                </section>
            </div>
        </div>
    )
}

/**
 * Updates the codeLinkCallout div with the public code.
 *
 * @param publicCode
 */
export function displayPublicCodePrompt(publicCode: string | null): () => void {
    logDebug(target, 'displayPublicCodePrompt', publicCode)
    const codeLinkCallout = el('#codeLinkCallout')
    const viewEl = bindQuerySelector(codeLinkCallout)
    viewEl('.codeLink').textContent = publicCode ?? '...'

    // A null code is the pre-fetch placeholder, not a dismissal: the prompt must
    // stay up showing '...' or a code request that never completes leaves the
    // device on a blank screen. Dismissal is the returned disposer.
    show(codeLinkCallout)

    const signInLink = viewEl('.signInLink') as HTMLAnchorElement
    const qrLink = viewEl('.qrCode') as HTMLAnchorElement
    if (publicCode) {
        const url = codeEntryUrl(currentTerritory(), publicCode)
        signInLink.href = url
        qrLink.href = url

        qrCodeSvg(url)
            .then((svg) => {
                qrLink.innerHTML = svg
            })
            .catch(errorHandler)
    } else {
        // Drop any previous code's link and QR — scanning a stale code next to
        // a '...' placeholder would send the user to a dead pairing.
        signInLink.removeAttribute('href')
        qrLink.removeAttribute('href')
        qrLink.innerHTML = ''
    }
    return () => hide(codeLinkCallout)
}
